/** Capacités renvoyées par GET /api/v1/me. `null` = pas de grant (compte legacy). */
export type CapabilityMap = Record<string, unknown> | null;

/** Tolère un JSONB renvoyé comme chaîne, et un objet vide. */
export function normalizeCapabilities(raw: unknown): CapabilityMap {
  if (raw == null) return null;
  let value = raw;
  if (typeof value === "string") {
    try {
      value = JSON.parse(value);
    } catch {
      return {};
    }
  }
  if (value == null || typeof value !== "object" || Array.isArray(value)) return {};
  return value as Record<string, unknown>;
}

const LEGACY_FORMATEUR_PATHS = new Set([
  "/",
  "/dashboard",
  "/calendar",
  "/sessions",
  "/learners",
  "/enrollments",
  "/rooms",
]);

export const TRAINER_CAPS = ["trainers.view", "trainers.own", "trainers.edit", "sessions.assign"];

/** Factures, TVA, budget : retirés de l’usage tant que le suivi financier n’est pas repris. */
export const FINANCE_ENABLED = false;

const FINANCE_PREFIXES = ["/invoices", "/reports/budget", "/reports/tva"];

function isFinancePath(pathname: string): boolean {
  return FINANCE_PREFIXES.some((prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`));
}

const PATH_CAPS: Array<{ prefix: string; anyOf: string[]; adminOnly?: boolean }> = [
  { prefix: "/settings/access", anyOf: [], adminOnly: true },
  { prefix: "/settings", anyOf: ["settings.edit"] },
  { prefix: "/import", anyOf: ["learners.import"] },
  { prefix: "/reports/budget", anyOf: ["reports.budget"] },
  { prefix: "/reports/tva", anyOf: ["reports.tva"] },
  { prefix: "/reports/eduqua", anyOf: ["eduqua.view"] },
  { prefix: "/health", anyOf: ["settings.edit"] },
  { prefix: "/opportunities", anyOf: ["pipeline.view"] },
  { prefix: "/trainers", anyOf: TRAINER_CAPS },
  { prefix: "/formations", anyOf: ["formations.view"] },
  { prefix: "/sessions", anyOf: ["sessions.view"] },
  { prefix: "/calendar", anyOf: ["sessions.view"] },
  { prefix: "/learners", anyOf: ["learners.view"] },
  { prefix: "/enrollments", anyOf: ["enrollments.view"] },
  { prefix: "/employers", anyOf: ["employers.edit"] },
  { prefix: "/invoices", anyOf: ["invoices.view"] },
  { prefix: "/rooms", anyOf: ["rooms.qr", "rooms.edit", "sessions.edit"] },
  { prefix: "/partners", anyOf: ["formations.view", "employers.edit"] },
];

export type NavCapabilityOpts = { anyOf?: string[]; adminOnly?: boolean; platformOnly?: boolean };

function hasCap(capabilities: Exclude<CapabilityMap, null>, code: string): boolean {
  return Object.prototype.hasOwnProperty.call(capabilities, code);
}

export function hasAnyCapability(
  role: string | undefined,
  capabilities: CapabilityMap,
  codes: string[]
): boolean {
  if (role === "admin") return true;
  if (capabilities) return codes.some((code) => hasCap(capabilities, code));
  return role !== "formateur";
}

export function canAccessPath(
  pathname: string,
  role: string | undefined,
  capabilities: CapabilityMap,
  platformAdmin = false
): boolean {
  if (!FINANCE_ENABLED && isFinancePath(pathname)) return false;
  if (pathname === "/platform" || pathname.startsWith("/platform/")) return platformAdmin;
  if (role === "admin") return true;
  if (pathname === "/" || pathname === "/dashboard") return true;
  const rule = PATH_CAPS.find((r) => pathname === r.prefix || pathname.startsWith(`${r.prefix}/`));
  if (!rule) return true;
  if (rule.adminOnly) return false;
  if (capabilities) {
    if (rule.anyOf.length === 0) return true;
    return rule.anyOf.some((code) => hasCap(capabilities, code));
  }
  if (pathname === "/trainers" || pathname.startsWith("/trainers/")) return false;
  if (role === "formateur") {
    return [...LEGACY_FORMATEUR_PATHS].some((p) => pathname === p || pathname.startsWith(`${p}/`));
  }
  return !rule.adminOnly;
}

export function canSeeNav(
  to: string,
  role: string | undefined,
  capabilities: CapabilityMap,
  opts?: NavCapabilityOpts,
  platformAdmin = false
): boolean {
  if (!FINANCE_ENABLED && isFinancePath(to)) return false;
  if (opts?.platformOnly) return platformAdmin;
  if (opts?.adminOnly) return role === "admin";
  if (role === "admin") return true;
  if (to === "/dashboard") return true;
  if (capabilities) {
    if (!opts?.anyOf?.length) return true;
    return opts.anyOf.some((code) => hasCap(capabilities, code));
  }
  if (to === "/trainers") return false;
  if (role === "formateur") return LEGACY_FORMATEUR_PATHS.has(to);
  return !opts?.adminOnly;
}

export function firstAllowedPath(
  role: string | undefined,
  capabilities: CapabilityMap,
  platformAdmin = false
): string {
  if (platformAdmin) return "/platform/schools";
  const candidates = [
    "/dashboard",
    "/sessions",
    "/calendar",
    "/learners",
    "/enrollments",
    "/rooms",
  ];
  return candidates.find((p) => canAccessPath(p, role, capabilities)) ?? "/dashboard";
}

/** Modules que cette personne verra dans la sidebar — aperçu pour l'admin. */
export function sidebarLabelsFor(
  role: string | undefined,
  capabilities: CapabilityMap,
  platformAdmin = false
): string[] {
  const items: Array<{ to: string; label: string; opts?: NavCapabilityOpts }> = [
    { to: "/platform/schools", label: "Écoles", opts: { platformOnly: true } },
    { to: "/dashboard", label: "Tableau de bord" },
    { to: "/calendar", label: "Calendrier", opts: { anyOf: ["sessions.view"] } },
    { to: "/opportunities", label: "Suivi", opts: { anyOf: ["pipeline.view"] } },
    { to: "/trainers", label: "Formateurs", opts: { anyOf: TRAINER_CAPS } },
    { to: "/formations", label: "Formations", opts: { anyOf: ["formations.view"] } },
    { to: "/sessions", label: "Sessions", opts: { anyOf: ["sessions.view"] } },
    { to: "/learners", label: "Apprenants", opts: { anyOf: ["learners.view"] } },
    { to: "/enrollments", label: "Inscriptions", opts: { anyOf: ["enrollments.view"] } },
    { to: "/employers", label: "Employeurs", opts: { anyOf: ["employers.edit"] } },
    { to: "/invoices", label: "Factures", opts: { anyOf: ["invoices.view"] } },
    { to: "/rooms", label: "Salles", opts: { anyOf: ["rooms.qr", "rooms.edit", "sessions.edit"] } },
    { to: "/partners", label: "Partenaires", opts: { anyOf: ["formations.view", "employers.edit"] } },
    { to: "/reports/budget", label: "Budget", opts: { anyOf: ["reports.budget"] } },
    { to: "/reports/tva", label: "TVA", opts: { anyOf: ["reports.tva"] } },
    { to: "/reports/eduqua", label: "Audit eduQua", opts: { anyOf: ["eduqua.view"] } },
    { to: "/settings", label: "Paramètres", opts: { anyOf: ["settings.edit"] } },
    { to: "/settings/access", label: "Habilitations", opts: { adminOnly: true } },
    { to: "/import", label: "Import Excel", opts: { anyOf: ["learners.import"] } },
  ];
  return items
    .filter((item) => canSeeNav(item.to, role, capabilities, item.opts, platformAdmin))
    .map((i) => i.label);
}
